import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import CompanyDetails from './components/CompanyDetails';
import ProfilePage from './components/ProfilePage';
import ProfileSetup from './components/ProfileSetup';
import CompanyInsights from './components/CompanyInsights';
import InsightsList from './components/InsightsList';
import OnboardingGoalFocus from './components/OnboardingGoalFocus';
import PricingPage from './components/PricingPage';
import MailerJobsPage from './components/MailerJobsPage';
import ApplicationsPage from './components/ApplicationsPage';
import RefundCancellationPolicyPage from './components/RefundCancellationPolicyPage';
import TermsConditionsPage from './components/TermsConditionsPage';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import AuthModal from './components/AuthModal';
import { AnalyticsTracker } from './analytics/AnalyticsTracker';
import RatingModal from './components/RatingModal';
import AppSidebar, { APP_SIDEBAR_WIDTH } from './components/AppSidebar';
import { appDesignTokens as tokens } from './components/appDesignTokens';


function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  return null;
}

function GlobalAuthModal() {
  const { isAuthModalOpen, closeLogin, authModalCallback } = useAuth();
  return (
    <AuthModal
      isOpen={isAuthModalOpen}
      onClose={closeLogin}
      onSuccess={authModalCallback}
    />
  );
}

function RequireAuth({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) {
    const postLoginRedirect = `${location.pathname || ''}${location.search || ''}`;
    return <Navigate to="/" replace state={{ postLoginRedirect }} />;
  }
  return children;
}

function AuthenticatedWithSidebar() {
  const { isDark } = useTheme();
  return (
    <RequireAuth>
      <div style={{ minHeight: '100vh', background: isDark ? '#0d1019' : tokens.colors.appShell }}>
        <AppSidebar topOffset={56} fixed />
        <div className="authenticated-sidebar-content" style={{ minWidth: 0, marginLeft: APP_SIDEBAR_WIDTH }}>
          <Outlet />
        </div>
      </div>
    </RequireAuth>
  );
}

function AuthenticatedWithSidebarAndHeaderOffset() {
  const { isDark } = useTheme();
  return (
    <RequireAuth>
      <div style={{ minHeight: '100vh', background: isDark ? '#0d1019' : tokens.colors.appShell }}>
        <AppSidebar topOffset={56} fixed />
        <div className="authenticated-sidebar-content" style={{ minWidth: 0, marginLeft: APP_SIDEBAR_WIDTH }}>
          <Outlet />
        </div>
      </div>
    </RequireAuth>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App compact-ui">
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsTracker />
            <GlobalAuthModal />
            <RatingModal />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/ref/:referralId" element={<LandingPage />} />
              <Route element={<AuthenticatedWithSidebarAndHeaderOffset />}>
                <Route path="/insights" element={<CompanyInsights />} />
                <Route path="/insights-list" element={<InsightsList />} />
              </Route>
              <Route element={<AuthenticatedWithSidebar />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/company-details" element={<CompanyDetails />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
              <Route path="/profile-setup" element={<RequireAuth><ProfileSetup /></RequireAuth>} />
              <Route path="/onboarding-goal-focus" element={<RequireAuth><OnboardingGoalFocus /></RequireAuth>} />
              <Route path="/pricing" element={<RequireAuth><PricingPage /></RequireAuth>} />
              <Route path="/jobs" element={<RequireAuth><MailerJobsPage /></RequireAuth>} />
              <Route path="/mailer-jobs" element={<RequireAuth><MailerJobsPage /></RequireAuth>} />
              <Route path="/applications" element={<RequireAuth><ApplicationsPage /></RequireAuth>} />
              <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicyPage />} />
              <Route path="/terms-conditions" element={<TermsConditionsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
        <Toaster position="top-center" richColors />
        <style>{`
          @media (max-width: 720px), (max-device-width: 720px) {
            .authenticated-sidebar-content {
              margin-left: 0 !important;
              padding-bottom: calc(74px + env(safe-area-inset-bottom)) !important;
            }
          }
        `}</style>
      </div>
    </ThemeProvider>
  );
}

export default App;
